/**
 * usePricesAdmin — данные и авто-сейв суб-таба «EO → Конфигуратор → Цены»
 * (A38: commit ячейки NumPadPop = запись сразу, кнопки «Сохранить» нет).
 * Поля ячеек — pn/pb/pp/mn (KQ/FLAB эталона js:54-55), wire — F2Q/F2V/F2M.
 *
 * Загрузка — Promise.allSettled 4 скоупов (quest_prices, спецверсии без
 * branch-фильтра, mc_prices, override услуг A41+): упавший скоуп не обнуляет
 * остальные; НЕзагруженный блокирует свои сейвы (blocked) — полнострочный
 * POST иначе перетёр бы реальную строку БД незагруженными нулями; тотальный
 * провал не поднимает loaded (скелетон, не «живые нули» A39), хосту — loadErr.
 * Мутации — run-паттерн useQuestsAdmin: optimistic over.* → запрос → flash
 * ячейки (lastK) / rollback+hint; в finally refetch. Сериализация chainRef —
 * обгон ответов полнострочных POST не откатит раннюю правку. noop-гард в
 * сейвах (эталон setCel); у saveOvr он же не даёт «подглядел-закрыл» (тап
 * мимо попа = commit) отчеканить override, равный сетевой цене.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import {
  ApiException, type ApiList, type ApiOk, type McPrice, type QuestPrice,
  type QuestVersion, type ServiceBranchPrice,
} from "../api/types";

export type PriceField = "pn" | "pb" | "pp" | "mn";
export const KQ: PriceField[] = ["pn", "pb", "pp", "mn"];
export const FLAB: Record<PriceField, string> = {
  pn: "До, чел", pb: "Базовая", pp: "+₽ за игрока", mn: "Мин. сумма",
};

const F2Q: Record<PriceField, keyof Omit<QuestPrice, "quest_id">> = {
  pn: "threshold", pb: "base_price", pp: "extra_per_player", mn: "min_price",
};
const F2V: Record<PriceField, "base_players" | "base_price" | "price_per_extra" | "min_price"> = {
  pn: "base_players", pb: "base_price", pp: "price_per_extra", mn: "min_price",
};
const F2M: Record<PriceField, keyof Omit<McPrice, "mc_id">> = {
  pn: "threshold", pb: "base_price", pp: "extra_per_player", mn: "min_price",
};

export type Cells = Record<PriceField, number>;
type Layer = Record<string, Partial<Record<PriceField, number | null>>>;
type Scope = "q" | "v" | "s" | "o";

interface Fetched {
  prices: Record<string, QuestPrice>;
  versions: QuestVersion[];
  mc: Record<string, McPrice>;
  ovr: Record<string, ServiceBranchPrice>; // ключ `${service_id}:${branch_id}`
  loaded: boolean;
  ok: Record<Scope, boolean> | null; // null до 1-го ответа; true — скоуп загружался, сейвы разрешены
}
const F0: Fetched = { prices: {}, versions: [], mc: {}, ovr: {}, loaded: false, ok: null };

export interface PricesAdmin {
  loaded: boolean;
  loadErr: boolean; // часть скоупов не загрузилась: их сейвы блокированы, хост кажет errHint
  hint: string | null;
  /** lastK-флэш сохранённой ячейки (зелёный cellsave): key + счётчик. */
  flash: { key: string; n: number } | null;
  versionsOf: (questId: string) => QuestVersion[];
  qPrice: (questId: string) => Cells;
  vPrice: (v: QuestVersion) => Cells;
  sPrice: (mcId: string) => Cells;
  /** Override поля услуги в филиале: число или null = наследование. */
  ovr: (mcId: string, branchId: string, f: PriceField) => number | null;
  hasOvr: (mcId: string) => boolean;
  saveQuest: (questId: string, f: PriceField, v: number) => void;
  saveVersion: (versionId: string, f: PriceField, v: number) => void;
  saveService: (mcId: string, f: PriceField, v: number) => void;
  saveOvr: (mcId: string, branchId: string, f: PriceField, v: number) => void;
  resetOvr: (mcId: string, branchId: string, f: PriceField) => void;
  refetch: () => void;
}

const byId = <T>(rs: T[], key: (x: T) => string): Record<string, T> =>
  Object.fromEntries(rs.map((r) => [key(r), r]));
const got = <T>(r: PromiseSettledResult<ApiList<T>>): T[] | null =>
  (r.status === "fulfilled" ? r.value.results ?? [] : null);
export function usePricesAdmin(): PricesAdmin {
  const [data, setData] = useState<Fetched>(F0);
  const [over, setOver] = useState<{ q: Layer; v: Layer; s: Layer; o: Layer }>({ q: {}, v: {}, s: {}, o: {} });
  const [hint, setHint] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const [tick, setTick] = useState(0);
  const pendingRef = useRef(0);
  const chainRef = useRef<Promise<unknown>>(Promise.resolve());
  const flashT = useRef(0);

  useEffect(() => {
    let dead = false;
    void Promise.allSettled([
      api<ApiList<QuestPrice>>("/api/prices/quests"),
      api<ApiList<QuestVersion>>("/api/quest-versions"),
      api<ApiList<McPrice>>("/api/prices/mc"),
      api<ApiList<ServiceBranchPrice>>("/api/service-prices"),
    ]).then(([rp, rv, rm, ro]) => {
      if (dead) return;
      const [p, v, m, o] = [got(rp), got(rv), got(rm), got(ro)] as const;
      setData((old) => ({
        prices: p ? byId(p, (x) => x.quest_id) : old.prices,
        versions: v ?? old.versions,
        mc: m ? byId(m, (x) => x.mc_id) : old.mc,
        ovr: o ? byId(o, (x) => `${x.service_id}:${x.branch_id}`) : old.ovr,
        loaded: old.loaded || !!(p || v || m || o),
        ok: { q: !!p || !!old.ok?.q, v: !!v || !!old.ok?.v, s: !!m || !!old.ok?.s, o: !!o || !!old.ok?.o },
      }));
      /* Полная свежая правда, мутаций в полёте нет — optimistic не нужен. */
      if (pendingRef.current === 0 && p && v && m && o) setOver({ q: {}, v: {}, s: {}, o: {} });
    });
    return () => { dead = true; };
  }, [tick]);
  useEffect(() => () => window.clearTimeout(flashT.current), []);

  const doFlash = (key: string) => {
    setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }));
    window.clearTimeout(flashT.current);
    flashT.current = window.setTimeout(() => setFlash(null), 900); // 900мс > 0.8s cellsave
  };

  /** Скоуп не загрузился: сейв собрал бы строку из пустоты — не шлём. */
  const blocked = (s: Scope): boolean => {
    if (!data.ok?.[s]) setHint("Цены не загрузились — потяните вниз, чтобы обновить. Правка не сохранена.");
    return !data.ok?.[s];
  };

  /** Обёртка мутации: flash при успехе, rollback+hint при ошибке, refetch всегда. */
  const run = (fn: () => Promise<string | null>, rollback: () => void) => {
    setHint(null);
    pendingRef.current += 1;
    chainRef.current = chainRef.current
      .then(fn)
      .then((key) => { if (key) doFlash(key); })
      .catch((err: unknown) => {
        rollback();
        setHint(err instanceof ApiException && err.status === 404
          ? "Не сохранилось: уже удалено другим пользователем. Список обновлён."
          : "Не сохранилось: ошибка сети. Значение возвращено.");
      })
      .finally(() => { pendingRef.current -= 1; setTick((n) => n + 1); });
  };

  const lay = (t: Scope, id: string, f: PriceField, v: number | null) =>
    setOver((o) => ({ ...o, [t]: { ...o[t], [id]: { ...o[t][id], [f]: v } } }));
  const unlay = (t: Scope, id: string, f: PriceField, v: number | null) => () =>
    setOver((o) => (o[t][id]?.[f] === v
      ? { ...o, [t]: { ...o[t], [id]: { ...o[t][id], [f]: undefined } } }
      : o)); // поверх уже более новая правка — не откатываем

  const cells = useCallback((base: Partial<Record<PriceField, number | null>>, layer?: Layer[string]): Cells => {
    const out = {} as Cells;
    for (const f of KQ) out[f] = (layer?.[f] !== undefined ? layer[f] : base[f]) ?? 0;
    return out;
  }, []);

  const qPrice = useCallback((id: string): Cells => {
    const p = data.prices[id];
    return cells({ pn: p?.threshold, pb: p?.base_price, pp: p?.extra_per_player, mn: p?.min_price }, over.q[id]);
  }, [data.prices, over.q, cells]);
  const vPrice = useCallback((v: QuestVersion): Cells =>
    cells({ pn: v.base_players, pb: v.base_price, pp: v.price_per_extra, mn: v.min_price }, over.v[v.id]),
  [over.v, cells]);
  const sPrice = useCallback((id: string): Cells => {
    const m = data.mc[id];
    return cells({ pn: m?.threshold, pb: m?.base_price, pp: m?.extra_per_player, mn: m?.min_price }, over.s[id]);
  }, [data.mc, over.s, cells]);

  const ovr = useCallback((mcId: string, branchId: string, f: PriceField): number | null => {
    const k = `${mcId}:${branchId}`;
    const l = over.o[k]?.[f];
    if (l !== undefined) return l;
    return data.ovr[k]?.[F2M[f]] ?? null;
  }, [data.ovr, over.o]);

  const hasOvr = useCallback((mcId: string): boolean =>
    [...new Set([...Object.keys(data.ovr), ...Object.keys(over.o)])].some((k) =>
      k.startsWith(`${mcId}:`) && KQ.some((f) => ovr(mcId, k.slice(mcId.length + 1), f) != null)),
  [data.ovr, over.o, ovr]);

  const versionsOf = useCallback((questId: string) =>
    data.versions.filter((v) => v.slot_quest_id === questId), [data.versions]);

  const saveQuest = (id: string, f: PriceField, v: number) => {
    const cur = qPrice(id);
    if (v === cur[f] || blocked("q")) return; // noop — эталон setCel
    lay("q", id, f, v);
    const next = { ...cur, [f]: v };
    run(async () => {
      await api<ApiOk>("/api/prices/quests", { method: "POST", body: { prices: [{
        quest_id: id, base_price: next.pb, threshold: next.pn, extra_per_player: next.pp, min_price: next.mn,
      }] } });
      return `q:${id}:${f}`;
    }, unlay("q", id, f, v));
  };

  const saveVersion = (id: string, f: PriceField, v: number) => {
    const cur = data.versions.find((x) => x.id === id);
    if ((cur && v === vPrice(cur)[f]) || blocked("v")) return;
    lay("v", id, f, v);
    run(async () => {
      await api<ApiOk>(`/api/quest-versions/${id}`, { method: "PUT", body: { [F2V[f]]: v } });
      return `s:${id}:${f}`;
    }, unlay("v", id, f, v));
  };

  const saveService = (id: string, f: PriceField, v: number) => {
    const cur = sPrice(id);
    if (v === cur[f] || blocked("s")) return;
    lay("s", id, f, v);
    const next = { ...cur, [f]: v };
    run(async () => {
      await api<ApiOk>("/api/prices/mc", { method: "POST", body: { prices: [{
        mc_id: id, base_price: next.pb, threshold: next.pn, extra_per_player: next.pp, min_price: next.mn,
      }] } });
      return `v:${id}:${f}`;
    }, unlay("s", id, f, v));
  };

  const saveOvr = (mcId: string, branchId: string, f: PriceField, v: number) => {
    /* noop-гард: «подглядел-закрыл» (тап мимо = commit) не чеканит override. */
    const cur = ovr(mcId, branchId, f);
    if ((cur !== null ? v === cur : v === sPrice(mcId)[f]) || blocked("o")) return;
    const k = `${mcId}:${branchId}`;
    lay("o", k, f, v);
    run(async () => {
      await api<ApiOk>(`/api/service-prices/${mcId}/${encodeURIComponent(branchId)}`, {
        method: "PUT", body: { [F2M[f]]: v },
      });
      return `o:${mcId}:${branchId}:${f}`;
    }, unlay("o", k, f, v));
  };

  /** Long-press «Сбросить к сетевой»: NULL-поле = наследование (без флэша). */
  const resetOvr = (mcId: string, branchId: string, f: PriceField) => {
    if (blocked("o")) return;
    const k = `${mcId}:${branchId}`;
    lay("o", k, f, null);
    run(async () => {
      await api<ApiOk>(`/api/service-prices/${mcId}/${encodeURIComponent(branchId)}`, {
        method: "PUT", body: { [F2M[f]]: null },
      });
      return null;
    }, unlay("o", k, f, null));
  };

  return {
    loaded: data.loaded, hint, flash, versionsOf, qPrice, vPrice, sPrice, ovr, hasOvr,
    loadErr: data.ok != null && !(data.ok.q && data.ok.v && data.ok.s && data.ok.o),
    saveQuest, saveVersion, saveService, saveOvr, resetOvr, refetch: () => setTick((n) => n + 1),
  };
}
